import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, useState, type JSX } from 'react';
import { UnterschriftenCheckboxState } from '../screens/Vorgang/Unterschriften';
import '../screens/Vorgang/Unterschriften.css';
import { formatDDMMYYYY } from '../shared/dateTime';
import { Vorgang } from '../types';
import { SPACING_BETWEEN_FORM_FIELDS } from './common/spacings';
import { Modal } from './Modal';
import { useDateiUrl } from '../hooks/useDateiUrl';
import { Signature } from './common/Signature';
import { Unterzeichner } from '../shared/constants';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly updateCheckboxState: (newState: Partial<UnterschriftenCheckboxState>) => void;
  readonly checkboxState: UnterschriftenCheckboxState;
};

export function AngebotZurAbtretung({ vorgang, isLoading, updateCheckboxState, checkboxState }: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxState.angebotZurAbtretung}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCheckboxState({ angebotZurAbtretung: event.target.checked })}
              name="angebotZurAbtretung"
              color="primary"
              data-testid="angebotZurAbtretungCheckbox"
            />
          }
          label={
            <>
              Ich habe{' '}
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  setIsModalOpen(true);
                }}
                data-testid="angebotZurAbtretungLink"
              >
                das Angebot zur Abtretung an das Sachverständigenbüro
              </Link>{' '}
              zur Kenntnis genommen und bin mit der Geltung einverstanden.*
            </>
          }
        />
      </Grid>
      {isModalOpen && <AngebotZurAbtretungModal vorgang={vorgang} isLoading={isLoading} isModalOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
    </>
  );
}

type ModalProps = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly isModalOpen: boolean;
  readonly onClose: () => void;
};

function AngebotZurAbtretungModal({ vorgang, isLoading, isModalOpen, onClose }: ModalProps): JSX.Element {
  const url = useDateiUrl(vorgang?.unterschriften?.angebotZurAbtretung?.key);

  const signDate = vorgang?.unterschriften?.angebotZurAbtretung?.datum ?? new Date().toISOString();

  return (
    <Modal
      openModal={isModalOpen}
      setOpenModal={onClose}
      title="Angebot zur Abtretung"
      body={
        <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} data-testid="angebotZurAbtretungModal">
          <Grid size={12}>
            <Typography variant="body2" data-testid="auftraggeberName">
              Hiermit biete ich – {vorgang.auftraggeber?.vorname} {vorgang.auftraggeber?.nachname} – an, die Schadenersatzforderung auf Erstattung der
              Sachverständigenkosten gegen:
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              <ol>
                <li>den Fahrer,</li>
                <li>den Halter,</li>
                <li>den Versicherer des am Unfall beteiligten Fahrzeugs,</li>
                <li>sowie im Fall des Kaskoschadens gegen die Versicherung des Geschädigten</li>
              </ol>
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">(vorstehende Personen gemäß Ziffer 1-4 werden im Folgenden auch „Anspruchsgegner“ genannt)</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2" data-testid="unfalldatum">
              aus dem Schadenereignis vom {formatDDMMYYYY(vorgang.unfalldaten.datum)} an Experts24 Tassone GmbH abzutreten.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">Die Annahmefrist beträgt 3 Jahre ab Erklärung meines Angebots zur Abtretung.</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Durch mein Angebot zur Abtretung und durch meine Zahlungsanweisung werde ich nicht von meiner Verpflichtung zur Zahlung der Sachverständigenkosten
              befreit, wenn die Anspruchsgegner nicht oder nicht in voller Höhe leisten. Jedoch wird die Sachverständigenforderung bis zur endgültigen Klärung
              mit der Versicherung des am Unfall beteiligten Fahrzeuges gestundet. Die Stundung endet mit Zahlungsablehnung der Versicherung. Nach dem Ende der
              Stundung kann Experts24 Tassone GmbH sofortige Zahlung von mir verlangen. Sollte Experts24 Tassone GmbH die Annahme meines Angebots zur Abtretung
              erklären, so werde ich dadurch auch von der Zahlung der Sachverständigenkosten befreit. Es besteht jedoch kein Anspruch darauf, dass Experts24
              Tassone GmbH die Annahme meines Angebots zur Abtretung erklärt. Experts24 Tassone GmbH steht es frei entweder mich auf Zahlung der
              Sachverständigenkosten in Anspruch zu nehmen oder die Annahme meines Angebots zu erklären.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Signature
              onSign={() => {}}
              onDeleteSignature={() => {}}
              signatureUrl={url}
              isLoading={isLoading}
              unterzeichnerAuswahl={vorgang?.unterschriften?.angebotZurAbtretung?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER}
              vorgang={vorgang}
              date={signDate}
              readOnly={true}
            />
          </Grid>
        </Grid>
      }
      footer={
        <div className="unterschriften__modal-button">
          <Button color="primary" variant="contained" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    />
  );
}
